<template>
  <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <h1 class="mb-0 mt-4" v-if="category && category.id">Kategorie bearbeiten</h1>
          <h1 class="mb-0 mt-4" v-if="category && !category.id">Kategorie erstellen</h1>
          <hr class="mt-0">
        </div>
      </div>
      <div class="row" v-if="category">
        <!-- Main area -->
        <div class="col-9">
          <div class="row">
            <div class="col-12">
              <ci-card type="dark">
                <label :for="`${_uid}-title`">Titel*</label>
                <input type="text" :class="getInputClass('title')" ref="title" :id="`${_uid}-title`" placeholder="Titel eingeben…" @change="setSlug(category.title)" v-model="category.title">

                <label class="mt-3" :for="`${_uid}-slug`">Slug*</label>
                <input type="text" :class="getInputClass('slug.slug')" ref="slug.slug" :id="`${_uid}-slug`" placeholder="Slug eingeben…" @blur="setSlug(category.slug.slug)" v-model="category.slug.slug">

                <label class="mt-3" :for="`${_uid}-teaser`">Teasertext</label>
                <textarea class="form-control bg-light" ref="teaser" :id="`${_uid}-teaser`" rows="3" v-model="category.teaser">
                </textarea>
              </ci-card>

            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-3">
          <ci-card type="dark">
            <ci-language-selector :language="language" @language-selector:change="onLanguageChange" />
            <ci-category-selector
              :selected-categories="parentCategory"
              :parent-selectable="true"
              :pre-selected-categories="category.parent ? [category.parent] : []"
              @category-selector:selected="onCategorySelected">
              Elternkategorie auswählen
            </ci-category-selector>
            <div class="form-group mt-2" v-if="!category.category_id">
              <label for="navigation-selector">Navigationsposition</label>
              <select class="form-control" id="navigation-selector" v-model="category.navigation">
                <option v-for="navigationPosition in navigationPositions" :key="navigationPosition.value" :value="navigationPosition.value">{{ navigationPosition.title }}</option>
              </select>
            </div>
          </ci-card>

          <ci-card class="mt-3" :type="getCardType('image_id')">
            <label>Teaserbild*</label>
            <ci-dropzone
              ref="image_id"
              v-if="!category.image"
              accept="image/*"
              @dropzone:files-selected="onFilesSelected" />
            <ci-image-focal
              v-else
              deletable
              :image="category.image"
              :focal-point="category.focalPoint"
              @image:delete="onImageDelete"
              @image:set-focalpoint="setFocalPoint" />
          </ci-card>

          <ci-card class="mt-3" :type="getCardType('color_scheme')">
            <ci-color-scheme ref="color_scheme" :selected-scheme="category.color_scheme" @color-scheme:selected="onSchemeSelected" />
          </ci-card>

          <ci-alerts class="mt-3" :messages.sync="messages" v-if="messages.length" />

          <div class="d-flex justify-content-end">
            <button class="btn btn-rounded-primary mt-3 ml-auto" @click="onSave">
              Speichern
            </button>
          </div>
        </div>
      </div>
  <div class="col-12 col-xs-12 col-md-12 col-xl-12 mt-4" v-if="category">
      <ci-modal v-if="showModal">
        <ci-modal-header>Bild löschen</ci-modal-header>
        <ci-modal-body>Möchten Sie das Bild wirklich löschen?</ci-modal-body>
        <ci-modal-footer>
          <button
            type="button"
            class="btn btn-secondary"
            data-dismiss="modal"
            @click="showModal = false"
          >Schließen</button>
          <button type="button" class="btn btn-danger" @click="deleteImage">Löschen</button>
        </ci-modal-footer>
      </ci-modal>
    </div>
  </div>
</template>

<script>
// components
import AlertMessages from '@/components/alerts/AlertMessages.vue';
import CategorySelector from '@/components/CategorySelector.vue';
import Card from '@/components/Card.vue';
import ColorScheme from '@/components/ColorScheme.vue';
import Dropzone from '@/components/Dropzone.vue';
import ImageFocal from '@/components/ImageFocal.vue';
import LanguageSelector from '@/components/LanguageSelector.vue';
import Modal from '@/components/Modal.vue';
import ModalBody from '@/components/ModalBody.vue';
import ModalHeader from '@/components/ModalHeader.vue';
import ModalFooter from '@/components/ModalFooter.vue';

// mixins
import createEdit from '@/mixins/createEdit';
import fieldValidation from '@/mixins/fieldValidation';
import getSlug from '@/mixins/getSlug';

// models
import CategoryModel from '@/models/CategoryModel';

// services
import CategoryService from '@/services/CategoryService';
import FileService from '@/services/FileService';

export default {
  name: 'CategoryCreateEdit',

  mixins: [createEdit, fieldValidation, getSlug],

  components: {
    'ci-alerts': AlertMessages,
    'ci-category-selector': CategorySelector,
    'ci-card': Card,
    'ci-color-scheme': ColorScheme,
    'ci-dropzone': Dropzone,
    'ci-image-focal': ImageFocal,
    'ci-language-selector': LanguageSelector,
    'ci-modal': Modal,
    'ci-modal-body': ModalBody,
    'ci-modal-footer': ModalFooter,
    'ci-modal-header': ModalHeader,
  },

  props: {
    id: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      availableColorCodes: [
        'orange',
        'green',
        'blue',
        'rose',
      ],
      categories: [],
      category: null,
      language: 'de',
      messages: [],
      navigationPositions: [
        {
          title: 'Header',
          value: 'header',
        },
        {
          title: 'Footer',
          value: 'footer',
        },
        {
          title: 'Ausgeblendet',
          value: null,
        },
      ],
      parentCategory: [],
      preSelectedCategories: [],
      updatedAt: null,
    };
  },

  created() {
    if (this.id) {
      this.getCategory();
    } else {
      this.category = new CategoryModel();
    }
  },

  watch: {
    id(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.getCategory();
      }
      if (newVal) {
        this.getCategory();
      } else {
        this.category = new CategoryModel();
      }
    },

    category: {
      handler() {
        this.resetValidation();
      },
      deep: true,
    },
  },

  methods: {
      deleteImage() {
      this.category.image = null;
      this.category.image_id = null;
      this.category.focal_point = {};
      this.showModal = false;
    },

    getCategory() {
      CategoryService.findOne(this.id)
        .then((category) => {
          this.category = new CategoryModel(category);
        });
    },

    onFilesSelected(files) {
      this.$emit('files-selected', files);
      FileService.uploadImages(files).then((response) => {
        this.category.image_id = response[0].id;
        this.category.set('image', response[0]);
      });
    },

    onCategorySelected(categories) {
      this.category.category_id = categories && categories.length ? categories[0].id : null;
      if (this.category.category_id) {
        this.category.navigation = null;
      }
    },

    onLanguageChange(payload) {
      this.category.language = payload;
    },

    onSave() {
      this.resetValidation();
      this.category.save()
        .then(() => {
          this.messages = [];
          this.messages.push({
            title: 'Super!',
            msg: 'Die Kategorie wurde erfolgreich gespeichert.',
            type: 'success',
          });
        })
        .catch((errors) => {
          this.messages = [];
          if (Array.isArray(errors)) {
            this.setInvalidFields(errors);
            this.messages.push({
              title: 'Fehler',
              msg: 'Es ist ein Fehler aufgetreten. Mindestens ein Pflichtfeld fehlt und wurde rot markiert.',
              type: 'danger',
            });
          }
        });
    },

    onSchemeSelected(scheme) {
      if (scheme === this.category.color_scheme) {
        this.category.color_scheme = null;
      } else {
        this.category.color_scheme = scheme;
      }
    },

    setFocalPoint(focalPoint) {
      this.category.focal_point = focalPoint;
    },

    setSlug(value) {
      this.category.slug.slug = this.getSlug(value);
    },
  },
};
</script>
